import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
// Notes system integration
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/Theme';
import { NotificationProvider } from './contexts/NotificationContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Skills from './pages/Skills';
import Feed from './pages/Feed';
import Sessions from './pages/Sessions';
import Chat from './pages/Chat';
import Notes from './pages/Notes';
import SavedFeeds from './pages/SavedFeeds';
import Payments from './pages/Payments';
import CreditHistory from './pages/CreditHistory';
import Feedback from './pages/Feedback';
import Admin from './pages/Admin';
import SkillCategories from './pages/SkillCategories';
import SystemReports from './pages/SystemReports';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return isAdmin ? <>{children}</> : <Navigate to="/dashboard" />;
};

const RootRedirect: React.FC = () => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (isAdmin) {
    return <Navigate to="/admin" />;
  } else {
    return <Navigate to="/feed" />;
  }
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <NotificationProvider>
          <Router>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
              <SessionTimeoutWarning />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Profile />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/skills"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Skills />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/feed"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Feed />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sessions"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Sessions />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/chat"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Chat />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/notes"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Notes />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/saved-feeds"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <SavedFeeds />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/payments"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Payments />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/credit-history"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <CreditHistory />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/feedback"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Feedback />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <Layout>
                        <Admin />
                      </Layout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/skill-categories"
                  element={
                    <AdminRoute>
                      <Layout>
                        <SkillCategories />
                      </Layout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/system-reports"
                  element={
                    <AdminRoute>
                      <Layout>
                        <SystemReports />
                      </Layout>
                    </AdminRoute>
                  }
                />
                <Route path="/" element={<RootRedirect />} />
              </Routes>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: 'linear-gradient(135deg, #1E40AF, #0D9488)',
                    color: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(30, 64, 175, 0.3)',
                  },
                }}
              />
            </div>
          </Router>
        </NotificationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
